import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { apiService } from '../services/ApiService';
import { ChatSession, type ChatState } from '../services/agent/ChatSession';
import { toolRunner } from '../services/agent/ToolRunner';
import { excelToolExecutor } from '../services/excel/ExcelToolExecutor';
import type { AgentContext, AuditView, Mode, ToolResult, UsageSummary } from '../types/agent';
import type { CircularBlock } from '../services/excel/convergence';

export interface UseAgentChatOptions {
  buildContext: () => Promise<AgentContext>;
  mode: Mode;
  onUsage?: (u: UsageSummary) => void;
  onThreadChanged?: (threadId: string | null) => void;
  getWorkbookId?: () => string | null;
  circularBlocks?: () => Promise<CircularBlock[]>;
  onConverged?: (info: { block_id: string; result: ToolResult }) => void;
  /** The sheets a batch wrote in: the index captures them next time whatever their fingerprint says. */
  onSheetsWritten?: (sheets: string[]) => void;
  /** Whether the agent is working on a turn: the index waits for it to stop before catching up (ChatSession). */
  onAgentWorking?: (working: boolean) => void;
  /** Rebuild the index after the server refused a turn for it; null when the turn can go again (ChatSession). */
  refreshIndex?: () => Promise<string | null>;
  /** Wait for the server to finish understanding the index after it refused a turn for that; null when the turn
   *  can go again (ChatSession). */
  awaitUnderstanding?: () => Promise<string | null>;
  /** The model a new thread runs on (PLAN-model-choice.md). */
  model?: string | null;
  /** The server refused the turn's model: the message to show. */
  onModelRefused?: () => string;
  /** The server refused the turn: the user already has `limit` turns streaming. The message to show. */
  onTurnsInProgress?: (limit: number) => string;
  /** The model provider cannot serve the turn; the team is on it. The message to show. */
  onServiceInterrupted?: () => string;
  /** A turn ended with an audit card: rewrite the Audit Findings sheet when the workbook has one (ChatSession). */
  onAuditCard?: (view: AuditView) => Promise<boolean>;
}

/** React binding of ChatSession (one session per pane lifetime). */
export function useAgentChat(opts: UseAgentChatOptions): ChatState & { session: ChatSession } {
  // Keep the latest callbacks/mode in refs so the session never captures stale values.
  const ref = useRef(opts);
  ref.current = opts;
  const session = useMemo(
    () =>
      new ChatSession({
        api: apiService,
        runner: toolRunner,
        buildContext: () => ref.current.buildContext(),
        getMode: () => ref.current.mode,
        getModel: () => ref.current.model ?? null,
        onModelRefused: () => ref.current.onModelRefused?.() ?? '',
        onTurnsInProgress: (limit) => ref.current.onTurnsInProgress?.(limit) ?? '',
        onServiceInterrupted: () => ref.current.onServiceInterrupted?.() ?? '',
        onUsage: (u) => ref.current.onUsage?.(u),
        onThreadChanged: (id) => ref.current.onThreadChanged?.(id),
        getWorkbookId: () => ref.current.getWorkbookId?.() ?? null,
        sheetFingerprint: (sheet) => excelToolExecutor.readSheetFingerprint(sheet),
        circularBlocks: () => ref.current.circularBlocks?.() ?? Promise.resolve([]),
        runLocal: (toolName, input) => toolRunner.run({ tool_call_id: `local-${Date.now()}`, tool_name: toolName, input, preview: '', risk: 'write', requires_confirmation: false }).then((e) => e.result),
        onConverged: (info) => ref.current.onConverged?.(info),
        onSheetsWritten: (sheets) => ref.current.onSheetsWritten?.(sheets),
        onAgentWorking: (working) => ref.current.onAgentWorking?.(working),
        refreshIndex: () => ref.current.refreshIndex?.() ?? Promise.resolve(null),
        awaitUnderstanding: () => ref.current.awaitUnderstanding?.() ?? Promise.resolve(null),
        onAuditCard: (view) => ref.current.onAuditCard?.(view) ?? Promise.resolve(false),
      }),
    [],
  );
  useEffect(() => () => void session.stop(), [session]);
  const state = useSyncExternalStore(
    (l) => session.subscribe(l),
    () => session.getState(),
    () => session.getState(),
  );
  return { ...state, session };
}
